import React, { FunctionComponent } from 'react'
import ComparisonResultCategory from '../../../entities/ComparisonResultCategory'
import ComparisonResultData from '../../../entities/ComparisonResultData'
import ComparisonResultSubcategory from '../../../entities/ComparisonResultSubcategory'
import useTranslation from '../../../hooks/useTranslation'
import filterRowsByOnlyDifferent from '../helpers/filterRows'
import ComparisonPdfTableRow from '../comparison-pdf-table-row/ComparisonPdfTableRow'
import style from './comparisonPdfTable.css'

export type Props = {
    category: ComparisonResultCategory
    categoryIndex: number
    columnCount: number
    onlyDifferent: boolean
    resultData: ComparisonResultData
}

const ComparisonPdfTable: FunctionComponent<Props> = (props) => {
    const { category, categoryIndex, columnCount, onlyDifferent, resultData } = props
    const { t } = useTranslation()

    const isModelCategory = category.id === 'model'
    const isFirstCategory = categoryIndex === 0
    const columnsNumber = Array.from({ length: columnCount }, (_, idx) => idx + 1)

    const renderSubcategoryHeader = (subcategory: ComparisonResultSubcategory, hasVisibleRows: boolean) => {
        if (isModelCategory || !subcategory.name || subcategory.name.trim() === '' || !hasVisibleRows) {
            return null
        }

        return (
            <tr className={style.resultRowCategory}>
                <td colSpan={columnCount + 1}>
                    {subcategory.name}
                </td>
            </tr>
        )
    }

    const renderCarlineNameRow = () => {
        if (!isModelCategory || !resultData?.info || resultData.info.length === 0 || columnCount === 0) {
            return null
        }

        if (!resultData.info.some(info => info.carlineName)) {
            return null
        }

        return (
            <tr className={style.infoRow}>
                <td className={style.infoHeaderCell}>{t('compare.info.carlineNameHeader')}</td>
                {Array.from({ length: columnCount }, (_, index) => (
                    <td key={`carlineName_${index}`} className={style.infoCell}>
                        {resultData.info?.[index]?.carlineName || ''}
                    </td>
                ))}
            </tr>
        )
    }

    const renderVehicleCodeRow = () => {
        if (!isModelCategory || !resultData?.info || resultData.info.length === 0 || columnCount === 0) {
            return null
        }

        if (!resultData.info.some(info => info.vehicleCode)) {
            return null
        }

        return (
            <tr className={style.infoRow}>
                <td className={style.infoHeaderCell}>{t(`compare.info.vehicleCodeHeader`)}</td>
                {Array.from({ length: columnCount }, (_, index) => (
                    <td key={`vehicleCode_${index}`} className={style.infoCell}>
                        {resultData.info?.[index]?.vehicleCode || ''}
                    </td>
                ))}
            </tr>
        )
    }

    return (
        <div className={style.categorySection}>
            {!isModelCategory && (
                <h3 className={style.categoryHeader}>
                    {t('compare.categories.' + category.id, category.name)}
                </h3>
            )}
            <table className={`${style.resultTable} ${style.alignedTable}`}>
                {columnCount > 0 && (
                    <colgroup>
                        <col className={style.firstColumn} style={{ width: `${(2 / (2 + columnCount)) * 100}%` }} />
                        {columnsNumber.map((num) => (
                            <col key={`col_${num}`} className={style.dataColumn} style={{ width: `${(1 / (2 + columnCount)) * 100}%` }} />
                        ))}
                    </colgroup>
                )}
                <tbody>
                    {isFirstCategory && columnsNumber.length > 0 && (
                        <tr className={style.columnHeaderRow}>
                            <td />
                            {columnsNumber.map((num) => (
                                <td key={`header_${num}`}>{num}</td>
                            ))}
                        </tr>
                    )}
                    {renderCarlineNameRow()}
                    {renderVehicleCodeRow()}
                    {category.subCategory.map((subcategory, subcategoryIndex) => {
                        const filteredRows = filterRowsByOnlyDifferent(subcategory.rows, onlyDifferent)

                        if (filteredRows.length === 0) {
                            return null
                        }

                        return (
                            <React.Fragment key={`${category.id}-${subcategory.id}-${subcategoryIndex}`}>
                                {renderSubcategoryHeader(subcategory, filteredRows.length > 0)}
                                {filteredRows.map((row) => (
                                    <ComparisonPdfTableRow key={row.id} row={row} />
                                ))}
                            </React.Fragment>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default ComparisonPdfTable
